import { useMemo, useRef, useState } from 'react'

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import LibraryAddCheckRoundedIcon from '@mui/icons-material/LibraryAddCheckRounded'
import SearchIcon from '@mui/icons-material/Search'
import { Checkbox, Icon, SlimButton, Tooltip } from 'components'

import * as S from './styles'

type OptionType = {
  label: string
  value: string
}

export type MultiselectMenuProps = {
  label: string
  searchPlaceholder?: string
  optionsList: OptionType[]
  selectedOptions: string[]
  onChange: (options: string[]) => void
  hasSearch?: boolean
  clearAction?: () => void
  checkAllAction?: () => void
  buttonAction?: () => void
  buttonActionLabel?: string
  required?: boolean
  disabled?: boolean
  disableOptions?: boolean
  hasBorder?: boolean
  fullWidth?: boolean
}

export const MultiselectMenu: React.FC<MultiselectMenuProps> = ({
  label,
  searchPlaceholder = 'Buscar',
  optionsList,
  selectedOptions,
  onChange,
  hasSearch = false,
  clearAction = undefined,
  checkAllAction = undefined,
  buttonAction = undefined,
  buttonActionLabel = 'Buscar',
  required = false,
  disabled,
  disableOptions = false,
  hasBorder = true,
  fullWidth = false,
}) => {
  const [searchValue, setSearchValue] = useState('')
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setTimeout(() => {
      setSearchValue('')
    }, 200)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'multiselect-popover' : undefined
  const requiredError = required && selectedOptions.length === 0
  const hasActions =
    clearAction !== undefined ||
    checkAllAction !== undefined ||
    buttonAction !== undefined ||
    requiredError

  const handleSelect = (option: string) => {
    if (selectedOptions.includes(option)) {
      onChange(selectedOptions.filter((item) => item !== option))
    } else {
      onChange([...selectedOptions, option])
    }
  }

  const handleClear = () => {
    setSearchValue('')
    if (clearAction !== undefined) clearAction()
  }

  const handleCheckAll = () => {
    if (checkAllAction !== undefined) checkAllAction()
  }

  const isOptionSelected = (option: string) => {
    return selectedOptions.includes(option)
  }

  const options = useMemo(() => {
    if (searchValue === '') return optionsList
    return optionsList.filter((opt) =>
      opt.label.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()),
    )
  }, [searchValue, optionsList])

  const getSelectedOptions = () => {
    const optionLabel = optionsList
      .find((opt) => opt.value === selectedOptions[0])
      ?.label.split(' - ')[0]
    if (selectedOptions.length === 0 || selectedOptions[0] === '') return ''
    if (selectedOptions.length === 1) return ` • ${optionLabel}`
    if (selectedOptions.length === optionsList.length) return ` • Todos`
    return ` • ${optionLabel} (+${selectedOptions.length - 1})`
  }

  const focusInput = () => {
    inputRef.current?.focus()
  }

  return (
    <S.Wrapper fullWidth={fullWidth}>
      <S.StyledButton
        aria-describedby={id}
        disabled={disabled}
        disableElevation
        endIcon={<KeyboardArrowDownIcon color="primary" />}
        hasBorder={hasBorder}
        onClick={handleClick}
        open={open}
        required={requiredError}
        variant="contained"
      >
        {label}
        {getSelectedOptions()}
      </S.StyledButton>
      <S.StyledPopover
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        elevation={0}
        id={id}
        onClose={handleClose}
        open={open}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
      >
        <S.PopoverContent>
          {hasSearch && (
            <S.InputWrapper>
              <S.InputContainer onClick={focusInput}>
                <input
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder={searchPlaceholder}
                  ref={inputRef}
                  value={searchValue}
                />
                <SearchIcon fontSize="small" />
              </S.InputContainer>
            </S.InputWrapper>
          )}
          <S.OptionsWrapper data-testid="multiselect-menu-options">
            {options.length ? (
              options.map((option, i) => (
                <S.OptionRow
                  data-testid={`option-row-${option.value}-${i}`}
                  key={`multiselect-option-${option.value}-${i}`}
                >
                  <Checkbox
                    checked={isOptionSelected(option.value)}
                    disabled={disableOptions}
                    onChange={() => handleSelect(option.value)}
                    size="small"
                  />
                  <S.OptionText>{option.label}</S.OptionText>
                </S.OptionRow>
              ))
            ) : (
              <S.OptionRow key={`multiselect-menu-option-not-found`}>
                <S.OptionText>
                  <strong>Nenhuma opção encontrada.</strong>
                </S.OptionText>
              </S.OptionRow>
            )}
          </S.OptionsWrapper>
          {requiredError && (
            <S.ActionsWrapper>
              <S.ErrorText>Ao menos uma opção deve estar selecionada.</S.ErrorText>
            </S.ActionsWrapper>
          )}
          {hasActions && (
            <S.ActionsWrapper>
              {checkAllAction && (
                <Tooltip arrow placement="top" title="Selecionar todos">
                  <div>
                    <SlimButton
                      disabled={disableOptions}
                      icon={<LibraryAddCheckRoundedIcon fontSize="small" />}
                      id="multiselect-menu-check-all-button"
                      onClick={handleCheckAll}
                      type="button"
                      variant="secondary"
                    />
                  </div>
                </Tooltip>
              )}
              {clearAction && (
                <Tooltip arrow placement="top" title="Limpar selecionados">
                  <div>
                    <SlimButton
                      disabled={disableOptions}
                      icon={<Icon icon="clear-all" size="20" />}
                      id="multiselect-menu-clear-button"
                      onClick={handleClear}
                      type="button"
                      variant="secondary"
                    />
                  </div>
                </Tooltip>
              )}
              {buttonAction && (
                <SlimButton
                  content={buttonActionLabel}
                  disabled={disableOptions}
                  id="multiselect-menu-search-button"
                  onClick={buttonAction}
                  type="button"
                  variant="primary"
                />
              )}
            </S.ActionsWrapper>
          )}
        </S.PopoverContent>
      </S.StyledPopover>
    </S.Wrapper>
  )
}
